/**
 * OpfsStore — the derived-asset cache: thumbnails and previews, keyed by hash.
 * Wraps the Origin Private File System and nothing else.
 */

import { forDom } from './bytes';

export interface BlobStore {
  put(key: string, bytes: Uint8Array): Promise<void>;
  get(key: string): Promise<Uint8Array | null>;
  getBlob(key: string): Promise<Blob | null>;
  has(key: string): Promise<boolean>;
  delete(key: string): Promise<void>;
  keys(): Promise<string[]>;
  totalBytes(): Promise<number>;
}

export class OpfsStore implements BlobStore {
  private dir: FileSystemDirectoryHandle | null = null;

  constructor(private readonly dirName = 'cache') {}

  private async root(): Promise<FileSystemDirectoryHandle> {
    this.dir ??= await (await navigator.storage.getDirectory()).getDirectoryHandle(this.dirName, {
      create: true,
    });
    return this.dir;
  }

  async put(key: string, bytes: Uint8Array): Promise<void> {
    const handle = await (await this.root()).getFileHandle(key, { create: true });
    const writable = await handle.createWritable();
    try {
      await writable.write(forDom(bytes));
    } finally {
      await writable.close();
    }
  }

  /**
   * A detached copy of the stored bytes — deliberately not the File itself.
   *
   * `handle.getFile()` hands back a live reference to the file on disk, and an
   * object URL made from it is lazy: the browser reads the bytes when the <img>
   * loads, not when the URL is created. `put` opens `createWritable()`, which
   * truncates the file to zero before writing. So a URL taken from a live File
   * turns into a broken image the moment anything rewrites that key — which is
   * exactly what the review screen showed on the first visit to a shoot, while
   * previews and thumbnails were still being generated underneath it.
   *
   * Copying costs a few hundred KB per preview and rules the whole class out:
   * no object URL anywhere in the app can be invalidated by a later write.
   */
  async getBlob(key: string): Promise<Blob | null> {
    try {
      const file = await (await this.root()).getFileHandle(key).then((h) => h.getFile());
      return new Blob([await file.arrayBuffer()], { type: file.type });
    } catch {
      return null;
    }
  }

  async get(key: string): Promise<Uint8Array | null> {
    try {
      const file = await (await this.root()).getFileHandle(key).then((h) => h.getFile());
      return new Uint8Array(await file.arrayBuffer());
    } catch {
      return null;
    }
  }

  /** Existence only — never reads the bytes, because every frame asks this. */
  async has(key: string): Promise<boolean> {
    try {
      await (await this.root()).getFileHandle(key);
      return true;
    } catch {
      return false;
    }
  }

  async delete(key: string): Promise<void> {
    await (await this.root()).removeEntry(key).catch(() => undefined);
  }

  async keys(): Promise<string[]> {
    const names: string[] = [];
    // @ts-expect-error — values() is async-iterable but not yet in every lib.dom
    for await (const entry of (await this.root()).values()) {
      if (entry.kind === 'file') names.push(entry.name);
    }
    return names;
  }

  async totalBytes(): Promise<number> {
    let total = 0;
    // @ts-expect-error — values() is async-iterable but not yet in every lib.dom
    for await (const entry of (await this.root()).values()) {
      if (entry.kind === 'file') total += (await entry.getFile()).size;
    }
    return total;
  }
}

/** In-memory stand-in used by tests and by private browsing where OPFS is absent. */
export class MemoryBlobStore implements BlobStore {
  private readonly map = new Map<string, Uint8Array>();

  async put(key: string, bytes: Uint8Array): Promise<void> {
    this.map.set(key, bytes);
  }
  async get(key: string): Promise<Uint8Array | null> {
    return this.map.get(key) ?? null;
  }
  async getBlob(key: string): Promise<Blob | null> {
    const bytes = this.map.get(key);
    return bytes ? new Blob([forDom(bytes)]) : null;
  }
  async has(key: string): Promise<boolean> {
    return this.map.has(key);
  }
  async delete(key: string): Promise<void> {
    this.map.delete(key);
  }
  async keys(): Promise<string[]> {
    return [...this.map.keys()];
  }
  async totalBytes(): Promise<number> {
    let total = 0;
    for (const bytes of this.map.values()) total += bytes.byteLength;
    return total;
  }
}
